import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import SelectDropdown from "../GlobalComponents/SelectDropdown";
import SimpleInput from "../SimpleInput";
import GradientBtn from "../WebsiteComponents/GradientBtn";
import { Option } from "react-multi-select-component";
import { IAddressData } from "src/types/GlobalInterfaces";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { setCurrentAddress } from "src/Store/cartSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/Store/store";
import { handleAddressSelect, validateAndFilterPhoneNumber } from "src/helpers/helperFunctions";
import {
  buildingNameRegex,
  countryRegex,
  flatVillaNoRegex,
  landmarkRegex,
  nameRegex,
  phoneNumberRegex,
  streetAddressRegex,
} from "src/lib/constans";
import CustomWebsiteDropdown from "../GlobalComponents/Widget/CustomWebsiteDropdown";

interface IProps {
  data: any;
  onConfirm: (data: IAddressData) => void;
  isEdit?: boolean;
  className?: string;
  btnContainerClass?: string;
  setIsEditAddress?: (isEditAddress: boolean) => void;
  setPickAddress?: (address: any) => void;
  onEditSuccess?: () => void;
  hasCustomPadding?: boolean;
}

const AddressForm = ({
  data,
  onConfirm,
  isEdit = false,
  className,
  btnContainerClass,
  setIsEditAddress,
  setPickAddress,
  onEditSuccess = () => {},
  hasCustomPadding = false,
}: IProps) => {
  const [addressData, setAddressData] = useState<IAddressData>({
    _id: "",
    addressType: "home",
    emirate: "",
    city: "",
    streetAddress: "",
    country: "",
    personName: "",
    mobileNumber: "",
    flatNo: "",
    buildingName: "",
    landmarks: "",
  });
  const [error, setError] = useState<{ [key: string]: string }>({});
  const [loader, setLoader] = useState<boolean>(false);

  useEffect(() => {
    setAddressData((prev) => ({
      ...prev,
      emirate: data?.emirate || "",
      city: data?.city || "",
      streetAddress: data?.address || data?.streetAddress || "",
      country: data?.country || "",
      personName: data?.personName || "",
      mobileNumber: data?.mobileNumber || "",
      flatNo: data?.flatNo || "",
      buildingName: data?.buildingName || "",
      landmarks: data?.landmarks || "",
      addressType: data?.addressType || "home",
      _id: data?._id || "",
    }));
  }, [data]);

  const addressType: Option[] = [
    { label: "Home", value: "home" },
    { label: "Work", value: "work" },
    { label: "Other", value: "other" },
  ];

  // Validation function
  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!addressData.addressType)
      newErrors.addressType = "Address type is required";
    if (!addressData.emirate) newErrors.emirate = "Emirate is required";
    if (!addressData.city) newErrors.city = "City is required";
    if (!addressData.streetAddress)
      newErrors.streetAddress = "Street address is required";
    else if (!streetAddressRegex.test(addressData.streetAddress))
      newErrors.streetAddress = "Street address should contain only letters, numbers, spaces, commas, periods, dashes, and slashes (10-100 characters)";
    if (!addressData.country) newErrors.country = "Country is required";
    else if (!countryRegex.test(addressData.country))
      newErrors.country = "Please enter a valid country";
    if (!addressData.flatNo) newErrors.flatNo = "Flat/Villa number is required";
    else if (!flatVillaNoRegex.test(addressData.flatNo))
      newErrors.flatNo = "Flat/Villa number should contain only letters, numbers, spaces, dashes, commas, and slashes (max 20 characters)";
    if (!addressData.buildingName)
      newErrors.buildingName = "Building name is required";
    else if (!buildingNameRegex.test(addressData.buildingName))
      newErrors.buildingName = "Building name should contain only letters, numbers, spaces, commas, and dashes (max 50 characters)";
    if (!addressData.personName) newErrors.personName = "Name is required";
    else if (!nameRegex.test(addressData.personName))
      newErrors.personName =
        "Name should contain only characters and spaces , b/w 2 to 20 characters";
    if (!addressData.mobileNumber)
      newErrors.mobileNumber = "Phone number is required";
    else if (!phoneNumberRegex.test(addressData.mobileNumber))
      newErrors.mobileNumber = "Please enter a valid phone number";
    
    // Validate landmarks if provided (optional field)
    if (addressData.landmarks && !landmarkRegex.test(addressData.landmarks))
      newErrors.landmarks = "Area/Landmark should contain only letters, numbers, spaces, commas, dashes, and slashes (max 100 characters)";

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const dispatch = useDispatch<AppDispatch>();

  const handleSave = async () => {
    if (validateForm()) {
      let payload = {
        addressType: addressData.addressType,
        emirate: addressData.emirate,
        streetAddress: addressData.streetAddress,
        flatNo: addressData.flatNo,
        buildingName: addressData.buildingName,
        landmarks: addressData.landmarks,
        country: addressData.country,
        mobileNumber: addressData.mobileNumber,
        personName: addressData.personName,
        city: addressData.city,
      };

      setLoader(true);

      let { res, err } = await httpRequest<{
        message: string;
        data: any;
        [key: string]: any;
      }>({
        path: isEdit
          ? `/customer-address/edit/${data?._id}`
          : "/customer-address/add-customer-address",
        method: isEdit ? "put" : "post",
        params: payload,
      });
      if (res) {
        toast("Address added successfully", { type: "success" });
        onConfirm(res?.data);
        if (isEdit) {
          setIsEditAddress?.(false);
          setPickAddress?.(null);
          onEditSuccess?.();
        } else {
          let isSelected = await handleAddressSelect(res?.data?._id, false);
          if (isSelected) {
            dispatch(setCurrentAddress(res?.data));
          }
        }
      } else {
        toast("Error adding address", { type: "error" });
      }
      setLoader(false);
    }
  };

  return (
    <>
      <div
        className={`w-full flex flex-col gap-4 ${
          hasCustomPadding ? "" : "p-2"
        } max-h-[50vh] overflow-y-auto ${className}`}
      >
        <div className="w-full h-fit bg-white shadow-all rounded-lg 2xl:rounded-xl res-padd">
          <div className="w-full res-padd-bottom border-b border-gray-300 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faLocationDot} size="lg" />
              <p className="font-Poppins-Medium font-size-18px text-[#202020]">
                Address Details
              </p>
            </div>
          </div>
          <div className="w-full flex flex-col res-padd-top">
            <CustomWebsiteDropdown
              showRadioBtn
              options={addressType}
              data={[
                {
                  label:
                    addressData.addressType.charAt(0).toUpperCase() +
                      addressData.addressType.slice(1) || "",
                  value: addressData.addressType || "",
                },
              ]}
              setData={(data: Option[]) => {
                setAddressData((prev) => ({
                  ...prev,
                  addressType: data[0]?.value || "",
                }));
                setError((prev) => ({ ...prev, addressType: "" }));
              }}
              labelText="Type"
              isRequired={true}
              placeholder="Select Type"
              optionId={(item: Option) => `address-type-${item.value}`}
              dropdownMainId="address-type-dropdown"
              newUI
              error={error.addressType}
            />
            <div className="w-full grid grid-cols-2 gap-2">
              <div className="w-full">
                <SimpleInput
                  onEnter={handleSave}
                  id="emirate-input"
                  labelText="Province"
                  isLabel
                  value={addressData.emirate}
                  onChange={(value) => {
                    // setAddressData((prev) => ({
                    //   ...prev,
                    //   emirate: value,
                    // }));
                    // setError((prev) => ({ ...prev, emirate: "" }));
                  }}
                  error={error.emirate}
                  newUI
                  isRequired
                  maxLength={50}
                  readOnly
                />
              </div>
              <div className="w-full">
                <SimpleInput
                  onEnter={handleSave}
                  id="city-input"
                  labelText="City"
                  isLabel
                  value={addressData.city}
                  onChange={(value) => {
                    // setAddressData((prev) => ({
                    //   ...prev,
                    //   city: value,
                    // }));
                    // setError((prev) => ({ ...prev, city: "" }));
                  }}
                  error={error.city}
                  newUI
                  isRequired
                  maxLength={50}
                  readOnly
                />
              </div>
            </div>
            <div className="w-full">
              <SimpleInput
                onEnter={handleSave}
                id="street-address-input"
                labelText="Street Address"
                isLabel
                value={addressData.streetAddress}
                onChange={(value) => {
                  // Filter to allow only letters, numbers, spaces, commas, periods, dashes, and slashes
                  const filteredValue = value.replace(/[^a-zA-Z0-9\s,+.\/-]/g, "");
                  setAddressData((prev) => ({
                    ...prev,
                    streetAddress: filteredValue,
                  }));
                  setError((prev) => ({ ...prev, streetAddress: "" }));
                }}
                error={error.streetAddress}
                newUI
                isRequired
                maxLength={100}
              />
            </div>
            <div className="w-full grid grid-cols-2 gap-2">
              <div className="w-full">
                <SimpleInput
                  onEnter={handleSave}
                  id="flat-villa-number-input"
                  labelText="Flat / House No"
                  isLabel
                  value={addressData.flatNo}
                  onChange={(value) => {
                    // Filter to allow only letters, numbers, spaces, dashes, commas, and slashes
                    const filteredValue = value.replace(/[^a-zA-Z0-9\s,\/-]/g, "");
                    setAddressData((prev) => ({
                      ...prev,
                      flatNo: filteredValue,
                    }));
                    setError((prev) => ({ ...prev, flatNo: "" }));
                  }}
                  error={error.flatNo}
                  newUI
                  isRequired
                  maxLength={20}
                />
              </div>
              <div className="w-full">
                <SimpleInput
                  onEnter={handleSave}
                  id="building-name-input"
                  labelText="Building Name"
                  isLabel
                  value={addressData.buildingName}
                  onChange={(value) => {
                    // Filter to allow only letters, numbers, spaces, commas, and dashes
                    const filteredValue = value.replace(/[^a-zA-Z0-9\s,-]/g, "");
                    setAddressData((prev) => ({
                      ...prev,
                      buildingName: filteredValue,
                    }));
                    setError((prev) => ({ ...prev, buildingName: "" }));
                  }}
                  error={error.buildingName}
                  newUI
                  isRequired
                  maxLength={50}
                />
              </div>
            </div>
            <div className="w-full">
              <SimpleInput
                onEnter={handleSave}
                id="landmark-input"
                labelText="Additional Information (Area, Landmark)"
                isLabel
                value={addressData.landmarks}
                onChange={(value) => {
                  // Filter to allow only letters, numbers, spaces, commas, dashes, and slashes
                  const filteredValue = value.replace(/[^a-zA-Z0-9\s,\/-]/g, "");
                  setAddressData((prev) => ({
                    ...prev,
                    landmarks: filteredValue,
                  }));
                  setError((prev) => ({ ...prev, landmarks: "" }));
                }}
                error={error.landmarks}
                newUI
                maxLength={100}
              />
            </div>
            <div className="w-full">
              <SimpleInput
                onEnter={handleSave}
                id="country-input"
                labelText="Country"
                isLabel
                value={addressData.country}
                onChange={(value) => {
                  // const filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
                  // setAddressData((prev) => ({
                  //   ...prev,
                  //   country: filteredValue,
                  // }));
                  // setError((prev) => ({ ...prev, country: "" }));
                }}
                error={error.country}
                newUI
                isRequired
                maxLength={56}
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Personal detail */}
        <div className="w-full h-fit bg-white shadow-all rounded-lg 2xl:rounded-xl res-padd">
          <div className="w-full res-padd-bottom border-b border-gray-300 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faLocationDot} size="lg" />
              <p className="font-Poppins-Medium font-size-18px text-[#202020]">
                Personal Details
              </p>
            </div>
          </div>
          <div className="w-full flex flex-col res-padd-top">
            <div className="w-full">
              <SimpleInput
                id="phone-input"
                labelText="Mobile Number"
                isLabel
                value={addressData.mobileNumber}
                onEnter={handleSave}
                onChange={(value) => {
                  const { filteredValue, errorMessage } = validateAndFilterPhoneNumber(value);
                  setAddressData((prev) => ({
                    ...prev,
                    mobileNumber: filteredValue,
                  }));
                  setError((prev) => ({
                    ...prev,
                    mobileNumber: errorMessage,
                  }));
                }}
                maxLength={14}
                minLength={11}
                error={error.mobileNumber}
                newUI
                isRequired
                placeholder="e.g., 03001234567 or +923001234567"
              />
            </div>
            <div className="w-full">
              <SimpleInput
                id="name-input"
                labelText="First & Last Name"
                isLabel
                value={addressData.personName}
                onEnter={handleSave}
                onChange={(value) => {
                  let filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
                  setAddressData((prev) => ({
                    ...prev,
                    personName: filteredValue,
                  }));
                  setError((prev) => ({ ...prev, personName: "" }));
                }}
                error={error.personName}
                newUI
                isRequired
                maxLength={50}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className={`w-full grid grid-cols-1 gap-4 res-padd-top ${btnContainerClass}`}
      >
        <GradientBtn
          loader={loader}
          id="save-address-btn"
          ButtonText={isEdit ? "Update Address" : "Save Address"}
          onPress={handleSave}
        />
      </div>
    </>
  );
};

export default AddressForm;
